import posthog from 'posthog-js';

// Event names kept in an enum to avoid typos and keep naming consistent
// across the codebase. Values mirror the prior Mixpanel event names so
// dashboards referencing these strings don't need to be rewritten.
export enum EventName {
  // Auth related events
  USER_LOGIN = 'UserLogin',
  USER_LOGIN_FAILED = 'UserLoginFailed',
  USER_REGISTRATION_STARTED = 'UserRegistrationStarted',
  USER_REGISTRATION_FAILED = 'UserRegistrationFailed',
  USER_SIGN_UP = 'UserSignUp',
  USER_ACCOUNT_CONFIRMED = 'UserAccountConfirmed',
  CONFIRMATION_CODE_RESENT = 'ConfirmationCodeResent',
  USER_LOGOUT = 'UserLogout',

  // MFA related events
  MFA_VERIFICATION_SUCCEEDED = 'MfaVerificationSucceeded',
  MFA_VERIFICATION_FAILED = 'MfaVerificationFailed',
  MFA_SETUP_STARTED = 'MfaSetupStarted',
  MFA_SETUP_COMPLETED = 'MfaSetupCompleted',

  // Password related events
  PASSWORD_RESET_REQUESTED = 'PasswordResetRequested',
  PASSWORD_RESET_COMPLETED = 'PasswordResetCompleted',

  // Project/Workspace related events
  WORKSPACE_CREATED = 'WorkspaceCreated',
  PROJECT_CREATION_STARTED = 'ProjectCreationStarted',
  PROJECT_CREATION_COMPLETED = 'ProjectCreationCompleted',
  PROJECT_CREATION_FAILED = 'ProjectCreationFailed',
  PROJECT_TEMPLATE_SELECTED = 'ProjectTemplateSelected',

  // Page view events
  PAGE_VIEW = 'PageView',
  ACCOUNT_SETTINGS_VIEWED = 'AccountSettingsViewed',
}

/**
 * Generic application-lifecycle events, fired from any "create application"
 * entry point outside onboarding (e.g. CreateApplicationModal). During
 * onboarding we fire `onboarding_app_*` instead — dashboards needing a
 * total can OR both events together.
 */
export const ApplicationEvent = {
  CREATED: 'application_created',
  CREATE_FAILED: 'application_create_failed',
} as const;

/** Onboarding funnel events. Keep in sync with PostHog dashboards. */
export const OnboardingEvent = {
  ORG_CREATE_FAILED: 'onboarding_org_create_failed',
  GIT_CONNECT_VIEWED: 'onboarding_git_connect_viewed',
  GIT_CONNECT_CLICKED: 'onboarding_git_connect_clicked',
  GIT_CONNECTED: 'onboarding_git_connected',
  GIT_CONNECT_FAILED: 'onboarding_git_connect_failed',
  GIT_CONNECT_CONTINUED: 'onboarding_git_connect_continued',
  APP_VIEWED: 'onboarding_app_viewed',
  APP_CREATED: 'onboarding_app_created',
  APP_CREATE_FAILED: 'onboarding_app_create_failed',
  SCAN_VIEWED: 'onboarding_scan_viewed',
  SCAN_START_CLICKED: 'onboarding_scan_start_clicked',
  SCAN_STARTED: 'onboarding_scan_started',
  SCAN_START_FAILED: 'onboarding_scan_start_failed',
} as const;

/**
 * Trust/assurance surfaces. `surface` identifies where the link was rendered so
 * the same event can be reused outside onboarding.
 */
export const TrustEvent = {
  LINK_CLICKED: 'trust_link_clicked',
} as const;

export const BillingEvent = {
  PLAN_VIEWED: 'billing_plan_viewed',
  SUBSCRIPTION_CHECKOUT_CLICKED: 'subscription_checkout_clicked',
  QUOTA_TOPUP_CHECKOUT_CLICKED: 'quota_topup_checkout_clicked',
} as const;

export const FeatureDepthEvent = {
  SCAN_START_CLICKED: 'scan_start_clicked',
} as const;

export const DemoEvent = {
  REPOSITORY_SELECTED: 'demo_repository_selected',
  SCAN_START_CLICKED: 'demo_scan_start_clicked',
  CONNECT_REPO_CLICKED: 'demo_connect_repo_clicked',
  APPLICATION_PAGE_VIEWED: 'demo_application_page_viewed',
  REPOSITORIES_PAGE_VIEWED: 'demo_repositories_page_viewed',
  SCAN_PAGE_VIEWED: 'demo_scan_page_viewed',
  SCANS_PAGE_VIEWED: 'demo_scans_page_viewed',
  FINDINGS_PAGE_VIEWED: 'demo_findings_page_viewed',
  FINDING_PAGE_VIEWED: 'demo_finding_page_viewed',
  FIXES_PAGE_VIEWED: 'demo_fixes_page_viewed',
  FIX_PAGE_VIEWED: 'demo_fix_page_viewed',
} as const;

export type EventProperties = Record<string, unknown>;

const key = process.env.REACT_APP_POSTHOG_KEY;
const host = process.env.REACT_APP_POSTHOG_HOST;
const enabled: boolean = Boolean(key && host);

if (enabled) {
  posthog.init(key as string, {
    api_host: host,
    autocapture: true,
    // Pageviews are captured explicitly from the router effect in App.tsx
    capture_pageview: false,
    capture_pageleave: true,
    persistence: 'localStorage+cookie',
    session_recording: {
      maskAllInputs: true,
    },
  });
}

/**
 * Thin wrapper around posthog-js that no-ops when PostHog is not configured.
 * Enabled iff REACT_APP_POSTHOG_KEY and REACT_APP_POSTHOG_HOST are both set.
 */
const PostHogService = {
  identify: (userId: string, properties?: EventProperties) => {
    if (!enabled) return;
    try {
      posthog.identify(userId, properties);
    } catch (error) {
      console.error('PostHog identify error', error);
    }
  },

  track: (eventName: EventName | string, properties: EventProperties = {}) => {
    if (!enabled) return;
    try {
      posthog.capture(eventName, properties);
    } catch (error) {
      console.error('PostHog track error', error);
    }
  },

  trackPageView: (path: string, properties: EventProperties = {}) => {
    if (!enabled) return;
    try {
      posthog.capture(EventName.PAGE_VIEW, {
        path,
        url: window.location.href,
        title: document.title,
        referrer: document.referrer,
        ...properties,
      });
    } catch (error) {
      console.error('PostHog trackPageView error', error);
    }
  },

  reset: () => {
    if (!enabled) return;
    try {
      posthog.reset();
    } catch (error) {
      console.error('PostHog reset error', error);
    }
  },
};

export default PostHogService;
